import { RegionsCode } from '../../constants/region';
import { Theme } from '../../constants/Theme';
import { UiLocale, UserId, Uuid } from '../../models/Base';
import { SessionTokens } from '../../models/SessionStore';
import { CaptchaData } from '../../models/SystemStore/SystemStore';
import { Email, Password, Phone } from '../../models/UserStore';
import { FollowershipJson } from '../../models/UserStore/Followership';
import {
  SystemInvitationJson,
  SystemInvitationType,
} from '../../models/UserStore/Invitation';
import { PhoneVerificationJson } from '../../models/UserStore/PhoneVerification';
import { ProfileJson } from '../../models/UserStore/Profile';
import { UserJson } from '../../models/UserStore/UserStore';
import { ModifiedPick, Optional, PartialPick } from '../../models/Utils';
import {
  CursorData,
  CursorParams,
  ProfileData,
  WithProfilesData,
  WithProfilesParams,
} from '../utils';

import { SpaceJson } from './../../models/SpaceStore/Space';
import restClient, { RestRequestOptions, RestResponse } from '.';

export interface PhoneParams {
  region: RegionsCode;
  phoneNumber: string;
}

interface PhoneVerificationData {
  phoneVerification: PhoneVerificationJson;
}

export const sendPhoneVerification = (
  body: PhoneParams,
  options?: RestRequestOptions,
): Promise<RestResponse<PhoneVerificationData>> => {
  return restClient.post('/users/phone-verification', body, options);
};

export interface VerifiedPhoneParams {
  region: RegionsCode;
  phoneNumber: string;
  verificationCode: string;
}
export interface PhoneValidationData {
  isValid: boolean;
}

export const phoneValidation = (
  body: VerifiedPhoneParams,
  options?: RestRequestOptions,
): Promise<RestResponse<PhoneValidationData>> => {
  return restClient.post('/users/phone-validation', body, options);
};

interface UserAndTokensData {
  user: UserJson;
  tokens: SessionTokens;
  isReactivated?: boolean;
  password?: string;
}

export const register = (
  body: {
    uiTheme: Theme;
    interfaceLocale: UiLocale;
    username?: string;
    email?: string;
    phone?: VerifiedPhoneParams;
    password?: string | boolean;
    webBoosterType?: 'normal' | 'bible';
    organization?: string;
    name?: string;
    source?: 'gdn' | 'gsearch' | 'facebook';
    userCode?: string;
    referralCode?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<UserAndTokensData>> => {
  return restClient.post(
    '/users',
    { ...body, client: process.env.REACT_APP_APP_CLIENT },
    options,
  );
};

interface RelatedAccountsData {
  mainAccount: UserJson;
  subaccounts: UserJson[];
}
export const login = (
  body: {
    username?: string;
    email?: string;
    phone?: PhoneParams;
    password?: string;
    appleOauth2Token?: string;
    facebookOauth2Token?: string;
    googleOauth2Token?: string;
    loginAsUserId?: UserId;
    userCode?: string;
    referralCode?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<RelatedAccountsData | UserAndTokensData>> => {
  return restClient.post(
    '/users/login',
    { ...body, client: process.env.REACT_APP_APP_CLIENT, ifNoSession: false },
    options,
  );
};

interface VerifyEmailResponse {
  email: Email;
}

export const verify = (
  body: { email: string; token: string },
  options?: RestRequestOptions,
): Promise<RestResponse<VerifyEmailResponse>> => {
  return restClient.post('/users/verify', body, options);
};

export const resetPasswordValidation = (
  body: { email: string; token: string },
  options?: RestRequestOptions,
): Promise<RestResponse<PhoneValidationData>> => {
  return restClient.post('/users/reset-password-validation', body, options);
};

export interface ResetPassswordResponse {
  email: Email;
  phone: Phone;
}

export const resetPassword = (
  body:
    | { password: string; email: string; token: string }
    | { password: string; phone: VerifiedPhoneParams },
  options?: RestRequestOptions,
): Promise<RestResponse<ResetPassswordResponse>> => {
  return restClient.post('/users/reset-password', body, options);
};

export interface FindProfileData {
  profile?: ProfileJson;
  email?: Email;
  phone?: Phone;
  password?: Password;
  isSubaccount?: boolean;
}

export const findProfile = (
  body: {
    username?: string;
    email?: string;
    phone?: PhoneParams;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<FindProfileData>> => {
  return restClient.get('/users/profile', body, options);
};

export interface ForgotPasswordData {
  password: Password;
}

export const forgotPassword = (
  body: { userId?: string; username?: string; email?: string },
  options?: RestRequestOptions,
): Promise<RestResponse<ForgotPasswordData>> => {
  return restClient.post('/users/forgot-password', body, options);
};

export const forgotPasswordWithPhone = (
  body: {
    userId?: string;
    username?: string;
    email?: string;
    phone?: PhoneParams;
    method: 'phone';
  },
  options?: RestRequestOptions,
): Promise<RestResponse<PhoneVerificationData>> => {
  return restClient.post('/users/forgot-password', body, options);
};

export const sendVerification = (
  body: { userId?: string; username?: string; email?: string },
  options?: RestRequestOptions,
): Promise<RestResponse<Email>> => {
  return restClient.post('/users/send-verification', body, options);
};

export interface UserCodeValidationData {
  profile: ProfileJson;
  isValid: boolean;
}

export const userCodeValidation = (
  body: { userCode: string },
  options?: RestRequestOptions,
): Promise<RestResponse<UserCodeValidationData>> => {
  return restClient.post('/users/user-code-validation', body, options);
};

interface SystemInvitationsData {
  systemInvitations: SystemInvitationJson[];
}

export const getSystemInvitations = <
  P extends {
    type?: SystemInvitationType;
    reverse?: boolean;
    followedUpBy?: UserId | UserId[];
  } & CursorParams,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationsData & CursorData>> => {
  return restClient.get(`/users/system-invitations`, params, options);
};

interface SystemInvitationData {
  systemInvitation: SystemInvitationJson;
}

export const createSystemInvitation = (
  body: ModifiedPick<
    SystemInvitationJson,
    'type',
    | 'reservedName'
    | 'email'
    | 'organization'
    | 'region'
    | 'followedUpBy'
    | 'category'
    | 'classroomId'
    | 'sender'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationData>> => {
  return restClient.post(`/users/system-invitations`, body, options);
};

interface ClassroomData {
  group: SpaceJson;
  membership: null;
  rootId: UserId;
}
interface GetSystemInvitationData {
  systemInvitation: SystemInvitationJson;
  classroom?: ClassroomData;
}

export const getSystemInvitation = <P extends Optional<WithProfilesParams>>(
  path: { systemInvitationId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<GetSystemInvitationData & WithProfilesData<P>>> => {
  return restClient.get(
    `/users/system-invitations/${path.systemInvitationId}`,
    params,
    options,
  );
};

export const updateSystemInvitation = (
  path: { systemInvitationId: Uuid },
  body: PartialPick<
    SystemInvitationJson,
    | 'reservedName'
    | 'email'
    | 'organization'
    | 'region'
    | 'followedUpBy'
    | 'category'
    | 'classroomId'
    | 'sender'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationData>> => {
  return restClient.patch(
    `/users/system-invitations/${path.systemInvitationId}`,
    body,
    options,
  );
};

export const deleteSystemInvitation = (
  path: { systemInvitationId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<{ systemInvitationId: null }>> => {
  return restClient.delete(
    `/users/system-invitations/${path.systemInvitationId}`,
    {},
    options,
  );
};

export const sentInvitationEmail = (
  path: { systemInvitationId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationData>> => {
  return restClient.post(
    `/users/system-invitations/${path.systemInvitationId}/send-invitation`,
    {},
    options,
  );
};
export const acceptInvitationEmail = (
  path: { systemInvitationId: Uuid },
  body: { classroomIds?: Uuid[] },
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationData>> => {
  return restClient.post(
    `/users/system-invitations/${path.systemInvitationId}/accept-invitation`,
    {},
    options,
  );
};
export const sentPasswordEmail = (
  path: { systemInvitationId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<SystemInvitationData>> => {
  return restClient.post(
    `/users/system-invitations/${path.systemInvitationId}/send-password`,
    {},
    options,
  );
};

interface ProfilesData<P> {
  profiles: P[];
}
interface FollowershipData {
  followeeFollowership: FollowershipJson | null;
  followerFollowership: FollowershipJson | null;
}
export const searchProfiles = (
  params: PartialPick<ProfileJson, 'role' | 'level' | 'region' | 'city'> & {
    key?: string;
    topics?: string[];
  } & CursorParams = {},
  options?: RestRequestOptions,
): Promise<
  RestResponse<ProfilesData<ProfileData & FollowershipData> & CursorData>
> => {
  return restClient.get('/users/search', params, options);
};

export const getCaptcha = (
  options?: RestRequestOptions,
): Promise<RestResponse<CaptchaData>> => {
  return restClient.get('/users/captcha/create', {}, options);
};
